import React, { useMemo, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { Bar } from "react-chartjs-2";
import "chartjs-plugin-datalabels";
import { sortBy } from "lodash";
import Toggle from "react-toggle";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec"
];

const colors = [
  "#CFE8F0",
  "#AFD9E7",
  "#8FCADD",
  "#6FBBD4",
  "#50ACCB",
  "#428DA7",
  "#336E82",
  "#254F5D",
  "#162F38"
];

const chartOptions = {
  plugins: {
    title: {
      display: false
    },
    datalabels: {
      color: "white"
    }
  },
  responsive: true,
  scales: {
    x: {
      stacked: true
    },
    y: {
      stacked: true
    }
  },
  legend: {
    display: true,
    position: "right"
  }
};

const HoursChart = ({ data }) => {
  const [stackChartMode, setStackChartMode] = useState(false); // false = Show Actual Hours / true = Show billable hours

  const handleChangeStackChartMode = () => {
    setStackChartMode(!stackChartMode);
  };

  const keyNames = [
    ...new Set(data.reduce((acc, curr) => [...acc, ...curr.users], []))
  ].sort();

  const monthsData = data.map((x) => {
    const breakdown = x.breakdown.reduce(
      (obj, it) => ({
        ...obj,
        [it.user]: it[stackChartMode ? "billableHours" : "actualHours"]
      }),
      {}
    );
    return {
      ...x,
      ...breakdown,
      index: x._id.month
        ? `${MONTHS[x._id.month - 1]} ${x._id.year}`
        : `${x._id.week} ${x._id.year}`
    };
  });

  const buildDataset = useCallback(
    (type, labelName, stack) =>
      keyNames.map((name, i) => ({
        label: `${name}${labelName}`,
        data: monthsData
          .map(
            (month) =>
              month.breakdown.find((user) => user.user === name)?.[type]
          )
          .filter(Boolean),
        backgroundColor: colors[i % colors.length],
        stack
      })),
    [keyNames, monthsData]
  );

  const chartData = useMemo(() => {
    if (monthsData.length > 6) {
      return {
        labels: monthsData.map((month) => month.index),
        datasets: stackChartMode
          ? sortBy(
              [...buildDataset("billableHours", "", "Stack 0")],
              (dataset) => dataset.label
            )
          : sortBy(
              [...buildDataset("actualHours", "", "Stack 0")],
              (dataset) => dataset.label
            )
      };
    }
    return {
      labels: monthsData.map((month) => month.index),
      datasets: sortBy(
        [
          ...buildDataset("billableHours", ": Billable", "Stack 0"),
          ...buildDataset("actualHours", ": Actual", "Stack 1")
        ],
        (dataset) => dataset.label
      )
    };
  }, [monthsData, buildDataset, stackChartMode]);

  return (
    <div>
      {monthsData.length > 6 && (
        <div className="toggle-wrapper">
          <span className={stackChartMode && "not-selected"}>Actual Hours</span>
          <Toggle
            name="stackChartMode"
            defaultChecked={false}
            onChange={handleChangeStackChartMode}
            checked={stackChartMode}
            value={stackChartMode}
            icons={false}
          />
          <span className={!stackChartMode && "not-selected"}>
            Billable Hours
          </span>
        </div>
      )}
      <div className="employee-summary-chart-container">
        <Bar options={chartOptions} data={chartData} />
      </div>
    </div>
  );
};

HoursChart.propTypes = {
  data: PropTypes.array
};

export default HoursChart;
